import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { participantsAPI, testsAPI } from '../services/api';
import Loading from '../components/Loading';
import UserAvatar from '../components/UserAvatar';
import '../styles/ParticipantTest.css';
import '../styles/ParticipantEntryStitch.css';

function ParticipantTest() {
  const { testUuid } = useParams();
  const [currentStep, setCurrentStep] = useState('info');
  const [test, setTest] = useState(null);
  const [participantName, setParticipantName] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTest();
  }, [testUuid]);

  const fetchTest = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await testsAPI.get(testUuid);
      if (response.data.success) setTest(response.data.test);
    } catch (requestError) {
      setError(requestError.error || 'تست یافت نشد یا منقضی شده است');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = () => {
    if (!participantName.trim()) {
      alert('لطفاً نام خود را وارد کنید');
      return;
    }
    setCurrentStep('test');
  };

  const handleSelectAnswer = (questionId, optionValue) => {
    setAnswers({ ...answers, [questionId]: optionValue });
  };

  const handleNextQuestion = () => {
    if (!answers[test.questions[currentQuestionIndex].id]) {
      alert('لطفاً یک گزینه انتخاب کنید');
      return;
    }

    if (currentQuestionIndex < test.questions.length - 1) {
      setCurrentQuestionIndex((index) => index + 1);
    } else {
      handleSubmitTest();
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) setCurrentQuestionIndex((index) => index - 1);
  };

  const handleSubmitTest = async () => {
    setSubmitting(true);
    try {
      const answersArray = Object.entries(answers).map(([questionId, value]) => ({ question_id: parseInt(questionId, 10), selected_value: value }));
      const response = await participantsAPI.submit(testUuid, participantName.trim(), answersArray);
      if (response.data.success) {
        setResult(response.data.result);
        setCurrentStep('result');
      }
    } catch (requestError) {
      alert(requestError.error || 'خطا در ثبت پاسخ‌ها');
    } finally {
      setSubmitting(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#3b82f6';
    if (score >= 40) return '#f59e0b';
    return '#ef4444';
  };

  const getScoreEmoji = (score) => {
    if (score >= 80) return '🏆';
    if (score >= 60) return '👏';
    if (score >= 40) return '👍';
    return '😅';
  };

  const getScoreMessage = (score) => {
    if (score >= 80) return 'عالی! تو خیلی خوب منو می‌شناسی! 🎉';
    if (score >= 60) return 'خوبه! اما هنوز جای پیشرفت داری! 😊';
    if (score >= 40) return 'متوسط! باید بیشتر با هم وقت بگذرونیم! 🤝';
    return 'اوه! به نظر میاد زیاد منو نمی‌شناسی! 😅';
  };

  const handleShare = async () => {
    const text = `من تست «${test.creator.name}» رو انجام دادم و ${result.score}% نمره گرفتم! ${getScoreEmoji(result.score)}\n\nتو هم امتحان کن:`;
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'چقدر منو می‌شناسی؟', text, url });
      } catch (shareError) {
        if (shareError.name !== 'AbortError') console.error('Share error:', shareError);
      }
    } else {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      alert('متن و لینک کپی شد!');
    }
  };

  if (loading) return <Loading message="در حال بارگذاری تست..." />;
  if (error) return <div className="participant-error"><div className="error-icon">😕</div><h2>متأسفانه تست یافت نشد!</h2><p>{error}</p><button onClick={() => { window.location.href = '/'; }} className="btn-go-home" type="button">رفتن به صفحه اصلی</button></div>;

  return (
    <div className="participant-test-container">
      {currentStep === 'info' && test && (
        <div className="info-section">
          <div className="info-card">
            <div className="participant-entry-creator">
              <UserAvatar name={test.creator.name} avatarUrl={test.creator.avatar_url || test.creator.profile_image} className="creator-avatar" />
              <div><h1>{test.creator.name}</h1><h2>{test.creator.name} دعوتت کرده به یه چالش تا ببینه چقدر می‌شناسیش! <span className="material-symbols-outlined">target</span></h2></div>
            </div>
            <div className="test-info">
              <div className="info-item"><span className="info-icon material-symbols-outlined">quiz</span><span>{test.questions.length.toLocaleString('fa-IR')} سؤال</span></div>
              <div className="info-item"><span className="info-icon material-symbols-outlined">groups</span><span>{(test.participants_count || 0).toLocaleString('fa-IR')} نفر شرکت کردند</span></div>
            </div>
            <div className="name-input-section"><label htmlFor="participant-name">نام شما:</label><input id="participant-name" type="text" placeholder="مثلاً: حسین" value={participantName} onChange={(event) => setParticipantName(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && handleStartTest()} autoFocus /></div>
            <button onClick={handleStartTest} className="btn-start-test" type="button"><span>شروع تست</span><span className="material-symbols-outlined">arrow_back</span></button>
            <p className="test-note"><span className="material-symbols-outlined">sentiment_very_satisfied</span>سعی کن صادقانه جواب بدی تا ببینیم واقعاً چقدر منو می‌شناسی!</p>
          </div>
          <div className="participant-entry-brand">چقدر منو می‌شناسی؟</div>
        </div>
      )}

      {currentStep === 'test' && test && (
        <div className="test-section">
          <div className="test-header"><h1>تست {test.creator.name}</h1><p>سؤال {currentQuestionIndex + 1} از {test.questions.length}</p></div>
          <div className="progress-container"><div className="progress-fill" style={{ width: `${((currentQuestionIndex + 1) / test.questions.length) * 100}%` }} /></div>
          <div className="question-card">
            <p className="question-sender-label">{test.creator.name} می‌پرسه: می‌دونی…</p>
            <h2 className="question-text">{test.questions[currentQuestionIndex].question}</h2>
            <div className="options-grid">
              {test.questions[currentQuestionIndex].options.map((option) => <button key={option.id} className={`option-button ${answers[test.questions[currentQuestionIndex].id] === option.value ? 'selected' : ''}`} onClick={() => handleSelectAnswer(test.questions[currentQuestionIndex].id, option.value)} type="button"><span className="option-emoji">{option.emoji}</span><span className="option-text">{option.text}</span></button>)}
            </div>
            <div className="question-navigation"><button onClick={handlePreviousQuestion} className="btn-nav btn-previous" disabled={currentQuestionIndex === 0} type="button">→ قبلی</button><button onClick={handleNextQuestion} className="btn-nav btn-next" disabled={!answers[test.questions[currentQuestionIndex].id]} type="button">{currentQuestionIndex === test.questions.length - 1 ? '✅ اتمام تست' : 'بعدی  ←'}</button></div>
          </div>
        </div>
      )}

      {currentStep === 'result' && result && (
        <div className="result-section"><div className="result-card">
          <div className="score-animation"><div className="score-circle-large" style={{ background: `conic-gradient(${getScoreColor(result.score)} ${result.score}%, #e2e8f0 ${result.score}%)` }}><div className="score-inner-large"><div className="score-emoji-large">{getScoreEmoji(result.score)}</div><div className="score-number-large">{result.score}%</div></div></div></div>
          <h2 className="result-title">{result.participant_name} عزیز</h2><p className="result-message">{getScoreMessage(result.score)}</p>
          <div className="result-stats"><div className="result-stat-item"><div className="stat-number" style={{ color: getScoreColor(result.score) }}>{result.correct_count}</div><div className="stat-label">سؤال درست</div></div><div className="result-stat-divider" /><div className="result-stat-item"><div className="stat-number" style={{ color: '#94a3b8' }}>{result.total_questions - result.correct_count}</div><div className="stat-label">سؤال غلط</div></div><div className="result-stat-divider" /><div className="result-stat-item"><div className="stat-number" style={{ color: '#667eea' }}>{result.total_questions}</div><div className="stat-label">کل سؤالات</div></div></div>
          <div className="creator-message"><p>ممنون که وقت گذاشتی و تست منو پر کردی!<br /><strong>{result.creator_name}</strong></p></div>
          <div className="result-actions"><button onClick={handleShare} className="btn-share-result" type="button">🔗 اشتراک‌گذاری نتیجه</button><button onClick={() => { window.location.href = '/'; }} className="btn-create-own" type="button">➕ من هم می‌خوام تست بسازم!</button></div>
          <div className="fun-fact"><p>{result.score >= 80 ? '🎉 واو! تو توی تاپ ۲۰٪ کسایی هستی که این تست رو دادن!' : result.score >= 60 ? '👏 خوبه! بیشتر از ۶۰٪ مردم از تو بهتر منو نمی‌شناسن!' : result.score >= 40 ? '😊 نه بد! میانگین هستی، اما می‌شه بهتر شد!' : '😅 یکم باید بیشتر وقت بگذرونید باهم!'}</p></div>
        </div></div>
      )}

      {submitting && <div className="submitting-overlay"><Loading message="در حال محاسبه نتیجه..." /></div>}
    </div>
  );
}

export default ParticipantTest;
